<template>
  <UiComponentWrapper class="page">
    <!-- Modal > Form + Download -->
    <Transition
      @enter="onEnter"
      @leave="onLeave"
    >
      <UiComponentModal
        v-if="isModalVisible && !isDownloadable"
        ref="$modal"
        @modal:close="isModalVisible = false"
      >
        <template #content>
          <div class="form">
            <OrganismHubspotNativeForm
              ref="$modalForm"
              :form-id="form.formId"
              unique-id="form-ebook-modal"
              @hubspot:form:ready="onModalFormReady"
              @hubspot:form:submitted="onSubmitted"
            />
            <div
              v-if="props.page.coMarketing"
              class="gdpr"
              v-html="props.page.rgpdFormText"
            />
          </div>
        </template>
      </UiComponentModal>
    </Transition>

    <Transition
      @enter="onEnter"
      @leave="onLeave"
    >
      <UiComponentModal
        v-if="isModalVisible && isDownloadable"
        ref="$modal"
        :class="props.page.coMarketing ? 'large' : 'extra-large'"
        @modal:close="isModalVisible = false"
      >
        <template #content>
          <div class="success">
            <UiComponentHeading
              class="title"
              :level="2"
            >
              {{
                props.componentForm.offerSuccessTitle
              }}
            </UiComponentHeading>
            <UiComponentWrapper class="cards">
              <OrganismContentCard
                :contains="false"
                class="resources"
                :class="{ split: !props.page.coMarketing }"
                :content="currentResource"
                display-on-modal
                force-new-tab
              />
              <OrganismContentCard
                v-if="!props.page.coMarketing"
                class="signup"
                :contains="false"
                :content="otherResource"
                crop="top"
                display-on-modal
              />
            </UiComponentWrapper>
          </div>
        </template>
      </UiComponentModal>
    </Transition>

    <!-- Section > Nav -->
    <UiComponentContainer class="nav">
      <!-- Logo(s) -->
      <UiComponentWrapper class="branding">
        <AtomLogo />
        <AtomAnchor
          v-if="props.page.coMarketing && props.page.coMarketingLogo"
          class="other-company"
          :to="props.page.coMarketingLink"
        >
          <UiComponentImage
            :key="`co-marketing-logo-size-${coMarketingLogoSize}`"
            alt="Co marketing Logo"
            :height="props.page.coMarketingLogo.height"
            :expected-size="coMarketingLogoSize"
            :lazyload="false"
            :orientation="
              props.page.coMarketingLogo.width > props.page.coMarketingLogo.height
                ? 'portrait'
                : 'landscape'
            "
            :source="props.page.coMarketingLogo.url"
            :width="props.page.coMarketingLogo.width"
          />
        </AtomAnchor>
      </UiComponentWrapper>

      <!-- Third logo (if exists) -->
      <UiComponentWrapper
        v-if="props.page.coMarketing && props.page.coMarketingLogo2"
        class="extra"
      >
        <AtomAnchor
          class="other-company"
          :to="props.page.coMarketingLink2"
        >
          <UiComponentImage
            alt="Co marketing Logo"
            :height="props.page.coMarketingLogo2.height"
            :expected-size="104"
            :lazyload="false"
            :orientation="
              props.page.coMarketingLogo2.width > props.page.coMarketingLogo2.height
                ? 'portrait'
                : 'landscape'
            "
            :source="props.page.coMarketingLogo2.url"
            :width="props.page.coMarketingLogo2.width"
          />
        </AtomAnchor>
      </UiComponentWrapper>

      <!-- Call To Action -->
      <UiComponentWrapper class="action">
        <AtomButton
          :action="downloadCallToAction.action"
          label="Navigation bar CTA"
          size="l"
          :to="downloadCallToAction.to"
          @click="isModalVisible = true"
        >
          {{ downloadCallToAction.slot }}
        </AtomButton>
      </UiComponentWrapper>
    </UiComponentContainer>
    <!-- End -->

    <!-- Section > Hero -->
    <UiComponentContainer class="jumbotron">
      <!-- Gradient -->
      <UiComponentWrapper class="gradient red" />
      <UiComponentWrapper class="gradient blue" />
      <!-- Content -->
      <OrganismHero
        :alignment="heroAlignment"
        :html="props.page.pageSubtitle"
        :primary-button="{
          ...downloadCallToAction,
          label: 'Header section CTA',
        }"
        :tag-with-image="tagWithImage"
        @primary:click="isModalVisible = true"
      >
        <template #tag>
          {{ props.page.resourceType.name }}
        </template>
        <template #heading>
          {{ props.page.pageTitle }}
        </template>
      </OrganismHero>
      <!-- Ebook -->
      <UiComponentWrapper class="image">
        <MoleculeEbook
          v-if="props.page.image"
          :image="props.page.image"
          :lazyload="false"
          :orientation="
            props.page.image.width > props.page.image.height ? 'landscape' : 'portrait'
          "
          :size="heroEbookSize"
        />
      </UiComponentWrapper>
    </UiComponentContainer>
    <!-- End -->

    <!-- Section > Form -->
    <UiComponentContainer class="form">
      <!-- Content -->
      <OrganismHero
        alignment="left"
        :html="props.page.descriptionFormSection"
        :level="2"
      >
        <template #heading>
          {{ props.page.titleFormSection }}
        </template>
      </OrganismHero>
      <!-- Form -->
      <UiComponentWrapper class="inner">
        <UiComponentWrapper
          v-if="!isDownloadable"
          class="step one"
        >
          <OrganismHubspotNativeForm
            ref="$pageForm"
            :form-id="form.formId"
            unique-id="form-ebook"
            @hubspot:form:ready="onPageFormReady"
            @hubspot:form:submitted="onSubmitted"
          />
          <div
            v-if="props.page.coMarketing"
            class="gdpr"
            v-html="props.page.rgpdFormText"
          />
        </UiComponentWrapper>
        <UiComponentWrapper
          v-else
          class="step two"
        >
          <UiComponentWrapper
            class="download"
            :class="{ 'co-marketing': props.page.coMarketing }"
          >
            <UiComponentHeading :level="3">
              {{ props.componentForm.offerSuccessTitle }}
            </UiComponentHeading>
            <UiComponentParagraph class="paragraph">
              {{
                props.page.shortDescription
              }}
            </UiComponentParagraph>
            <AtomButton
              force-new-tab
              icon-position="icon-right"
              size="l"
              :to="
                localePath({
                  name: 'file-slug',
                  params: {
                    slug: props.page.ebook?.slug,
                  },
                })
              "
            >
              <UiComponentIcon type="download" />
              {{ props.hub.ctaDownload }}
            </AtomButton>
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>
    </UiComponentContainer>
    <!-- End -->

    <!-- Section > Slider -->
    <UiComponentContainer class="sample">
      <!-- Content -->
      <OrganismHero
        alignment="left"
        :html="props.page.sliderSectionDescription"
        :level="2"
        :primary-button="{
          ...downloadCallToAction,
          label: 'Slider section CTA',
        }"
        @primary:click="isModalVisible = true"
      >
        <template #heading>
          {{ props.page.sliderSectionTitle }}
        </template>
      </OrganismHero>
      <!-- Slider -->
      <OrganismSlider
        ref="$slider"
        @slider:body:click="isFullScreenSliderVisible = screenWidth >= 1328"
        @slider:index:change="sliderIndex = $event"
      >
        <UiComponentImage
          v-for="(image, index) in props.page.slider"
          :key="`slider-${index}-size-${sliderSize}`"
          :alt="image.alt"
          class="slide"
          :expected-size="sliderSize"
          :height="image.height"
          :source="image.url"
          :width="image.width"
        />
      </OrganismSlider>
    </UiComponentContainer>
    <!-- End -->

    <!-- Popup > Slider -->
    <UiComponentWrapper
      v-if="isFullScreenSliderVisible"
      class="popup"
    >
      <UiComponentWrapper
        class="background"
        @click="isFullScreenSliderVisible = false"
      />
      <UiComponentWrapper
        class="slider"
        :style="{
          maxHeight: `${popupSliderMaxHeight}px`,
          maxWidth: `${popupSliderMaxWidth}px`,
        }"
      >
        <UiComponentWrapper
          class="close"
          @click="isFullScreenSliderVisible = false"
        >
          <UiComponentIcon type="x" />
        </UiComponentWrapper>
        <UiComponentWrapper class="inner">
          <UiComponentWrapper
            class="slides"
            :style="{
              width: `${popupSliderMaxWidth * props.page.slider.length}px`,
              transform: `translateX(-${
                (sliderIndex / props.page.slider.length) * 100
              }%)`,
            }"
          >
            <UiComponentImage
              v-for="(image, index) in props.page.slider"
              :key="`popup-slider-${index}-size-${sliderSize}`"
              :alt="image.alt"
              class="slide"
              :class="{ active: sliderIndex === index }"
              :expected-size="popupSliderMaxWidth"
              :height="image.height"
              :lazyload="false"
              :source="image.url"
              :width="image.width"
            />
          </UiComponentWrapper>
        </UiComponentWrapper>
        <UiComponentWrapper class="navigation">
          <UiComponentIcon
            :class="{ active: sliderIndex !== 0 }"
            type="chevron-left"
            @click="
              sliderIndex === 0
                ? (sliderIndex = sliderIndex)
                : (sliderIndex = sliderIndex - 1)
            "
          />
          <UiComponentWrapper class="bullets">
            <UiComponentWrapper
              v-for="n in props.page.slider.length"
              :key="n"
              class="bullet"
              :class="{ active: sliderIndex === n - 1 }"
              @click="sliderIndex = n - 1"
            />
          </UiComponentWrapper>
          <UiComponentIcon
            :class="{ active: sliderIndex !== props.page.slider.length - 1 }"
            type="chevron-right"
            @click="
              sliderIndex === props.page.slider.length - 1
                ? (sliderIndex = sliderIndex)
                : (sliderIndex = sliderIndex + 1)
            "
          />
        </UiComponentWrapper>
      </UiComponentWrapper>
    </UiComponentWrapper>
    <!-- End -->

    <!-- Section > Preview (Only displayed if the title and the image has been declared) -->
    <UiComponentWrapper
      v-if="
        (props.page.resourcePreviewTitle || props.page.resourcePreviewDescription)
          && props.page.resourceSectionCover
      "
      class="preview"
    >
      <UiComponentContainer class="inner">
        <!-- Content -->
        <OrganismHero
          v-if="props.page.resourcePreviewTitle"
          alignment="left"
          :html="props.page.resourcePreviewDescription"
          :level="2"
          :primary-button="{
            ...downloadCallToAction,
            label: 'Resource preview section CTA',
          }"
          @primary:click="isModalVisible = true"
        >
          <template #heading>
            {{ props.page.resourcePreviewTitle }}
          </template>
        </OrganismHero>
        <OrganismHero
          v-else
          alignment="left"
          :html="props.page.resourcePreviewDescription"
          :level="2"
          :primary-button="{
            ...downloadCallToAction,
            label: 'Resource preview section CTA',
          }"
          @primary:click="isModalVisible = true"
        />
        <!-- Illustration -->
        <UiComponentImage
          :key="`preview-illustration-size-${previewSize}`"
          :alt="props.page.resourceSectionCover.alt"
          class="illustration"
          :expected-size="previewSize"
          :height="props.page.resourceSectionCover.height"
          :source="props.page.resourceSectionCover.url"
          :width="props.page.resourceSectionCover.width"
        />
      </UiComponentContainer>
    </UiComponentWrapper>
    <!-- End -->

    <!-- Section > Discover (If Co Marketing option is activated) -->
    <UiComponentContainer
      v-if="props.page.coMarketing"
      class="discover"
      :start="1"
      :end="1"
    >
      <UiComponentWrapper class="inner">
        <UiComponentImage
          v-if="props.componentDiscoverTheOffer.background"
          :key="`discover-icon-size-${discoverEbookSize}`"
          class="ebook"
          :alt="props.componentDiscoverTheOffer.background.alt"
          :expected-size="discoverEbookSize"
          :extra-params="{ trim: 'color' }"
          :height="props.componentDiscoverTheOffer.background.height"
          orientation="portrait"
          :source="props.componentDiscoverTheOffer.background.url"
          use-natural-width
          :width="props.componentDiscoverTheOffer.background.width"
        />
        <UiComponentHeading
          as="paragraph"
          :level="4"
        >
          {{
            props.page.promoteSectionTitle
              || (typeOfContent.charAt(0) === "R"
                ? props.componentDiscoverTheOffer.titleForReports
                : props.componentDiscoverTheOffer.titleForEbooks)
          }}
        </UiComponentHeading>
        <div
          v-if="props.page.promoteSectionText"
          class="description"
          v-html="props.page.promoteSectionText"
        />
        <UiComponentParagraph
          v-else
          class="description"
        >
          {{
            props.componentDiscoverTheOffer.description
          }}
        </UiComponentParagraph>
        <AtomButton
          :action="downloadCallToAction.action"
          label="Promote section CTA"
          size="l"
          :to="downloadCallToAction.to"
          type="text"
          @click="isModalVisible = true"
        >
          {{ props.page.promoteSectionCta || props.componentDiscoverTheOffer.ctaWording
          }}<UiComponentIcon type="arrow-right" />
        </AtomButton>
      </UiComponentWrapper>
    </UiComponentContainer>
    <!-- End -->

    <!-- Section > Get Started (Otherwise) -->
    <UiComponentContainer
      v-else
      class="get-started"
      :start="1"
      :end="1"
    >
      <OrganismCallToAction
        :features="getStartedFeatureSet"
        :primary-button="{
          ...primaryCallToAction,
          label: 'Big section CTA',
        }"
        :secondary-button="{
          ...secondaryCallToAction,
          label: 'Big section CTA',
        }"
      >
        <template #heading>
          {{ props.componentGetStarted.title }}
        </template>
        <template #paragraph>
          {{ props.componentGetStarted.paragraph }}
        </template>
      </OrganismCallToAction>
    </UiComponentContainer>
    <!-- End -->

    <!-- Section > Related Content -->
    <UiComponentWrapper class="related-content">
      <UiComponentContainer class="inner">
        <UiComponentHeading :level="3">
          {{ props.componentGlobal.youMayAlsoLike }}
        </UiComponentHeading>
        <UiComponentWrapper class="list">
          <OrganismContentCard
            v-for="content in resources"
            :key="content.id"
            class="item"
            :content="content"
            crop="top"
          />
        </UiComponentWrapper>
      </UiComponentContainer>
    </UiComponentWrapper>
    <!-- End -->
  </UiComponentWrapper>
</template>

<script setup>
import { gsap } from 'gsap'

// Props
const props = defineProps({
  componentDiscoverTheOffer: {
    required: true,
    type: Object,
  },
  componentForm: {
    required: true,
    type: Object,
  },
  componentGetStarted: {
    required: true,
    type: Object,
  },
  componentGlobal: {
    required: true,
    type: Object,
  },
  componentOfferForm: {
    required: true,
    type: Object,
  },
  componentRelatedContent: {
    required: true,
    type: Object,
  },
  hub: {
    required: true,
    type: Object,
  },
  page: {
    required: true,
    type: Object,
  },
})

// Composables
const { page, track } = useAnalyticsJS()
const { locale } = useI18n()
const { isLogged } = useIsLoggedIn()
const localePath = useLocalePath()
const { $slugify } = useNuxtApp()
const { public: publicRuntimeConfig } = useRuntimeConfig()

// Refs
const $modal = ref(null)
const $modalForm = ref(null)
const $pageForm = ref(null)
const $slider = ref(null)
const coMarketingLogoSize = ref(104)
const isDownloadable = ref(false)
const isFullScreenSliderVisible = ref(false)
const isModalVisible = ref(false)
const screenHeight = ref(640)
const screenWidth = ref(360)
const sliderIndex = ref(0)

// Computed Properties
const currentResource = computed(() => {
  return {
    backgroundImage: props.page.backgroundImage,
    callToAction: props.page?.resourceType?.cta,
    description: props.page.shortDescription,
    image: props.page?.image,
    tagColor: props.page?.resourceType?.color,
    tagIcon: props.page?.resourceType?.icon,
    to: localePath({
      name: 'file-slug',
      params: {
        slug: props.page.ebook?.slug,
      },
    }),
    type: props.page.resourceType?.name,
  }
})

const downloadCallToAction = computed(() => {
  return {
    action: 'Offer clicked',
    slot: props.hub.ctaDownload,
    to: '#',
  }
})

const discoverEbookSize = computed(() => {
  return screenWidth.value >= 1328 ? 198 : 222
})

const form = computed(() => {
  return props.page.form || props.componentOfferForm
})

const getStartedFeatureSet = computed(() => {
  return [props.componentGlobal.noCreditCard, props.componentGlobal.noInstall]
})

const heroAlignment = computed(() => {
  return screenWidth.value >= 1328 ? 'left' : 'center'
})

const heroEbookSize = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return screenWidth.value - 16 * 2
    case screenWidth.value >= 768 && screenWidth.value < 1328:
      return screenWidth.value - 24 * 2
    default:
      return 469
  }
})

const otherResource = computed(() => {
  return {
    action: 'Signup clicked',
    backgroundImage: props.componentForm.offerSuccessCoMarketing,
    description: props.componentForm.offerSuccessText,
    callToAction: props.componentGlobal.ctaTryForFreeShort,
    to: `https://${publicRuntimeConfig.appBaseUrl}/#/signup`,
  }
})

const popupSliderMaxHeight = computed(() => {
  return props.page.slider.length === 0 ? 0 : screenHeight.value - 73 * 2
})

const popupSliderMaxWidth = computed(() => {
  const aspectRatio
    = props.page.slider.length === 0
      ? 1
      : props.page.slider[0].width / props.page.slider[0].height
  return popupSliderMaxHeight.value * aspectRatio
})

const previewSize = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return screenWidth.value - 16 * 2
    case screenWidth.value >= 768 && screenWidth.value < 1328:
      return screenWidth.value - 24 * 2
    default:
      return 550
  }
})

const primaryCallToAction = computed(() => {
  return isLogged.value
    ? {
        action: 'Upgrade clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/settings?page=settings&tab=billing`,
        slot: props.componentGlobal.ctaUpgradeNow,
      }
    : {
        action: 'Signup clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/signup`,
        slot: props.componentGlobal.ctaTryForFreeShort,
      }
})

const resources = computed(() => {
  return [...props.page.relatedContent, ...props.componentRelatedContent]
    .map(resource => ({
      id: resource.id,
      backgroundImage: resource.backgroundImage,
      callToAction: resource.resourceType.cta,
      description: resource.shortDescription,
      image: resource.image,
      tagColor: resource.resourceType.color,
      tagIcon: resource.resourceType.icon,
      title: resource.shortTitle,
      to: resource.slug.includes('http')
        ? resource.slug
        : localePath({
            name: 'resources-type-slug',
            params: {
              slug: resource.slug,
              type: $slugify(resource.resourceType.name),
            },
          }),
      topic: resource.resourceTopic.name,
      type: resource.resourceType.name,
    }))
    .slice(0, 3)
})

const secondaryCallToAction = computed(() => {
  return {
    action: 'Demo clicked',
    slot: props.componentGlobal.ctaGetDemo,
    to: '#form-demo',
  }
})

const sliderSize = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return screenWidth.value - 16 * 2
    case screenWidth.value >= 768 && screenWidth.value < 1328:
      return screenWidth.value - 24 * 2
    default:
      return 510
  }
})

const tagWithImage = computed(() => {
  return {
    icon: props.page.resourceType.icon,
    color: props.page.resourceType.color,
  }
})

const trackName = computed(() => {
  return props.page.trackName || props.page.trackNameFallback
})

const typeOfContent = computed(() => {
  return props.page.resourceType._allNameLocales.find(
    type => type.locale === locale.value,
  ).value
})

// Watchers
watch(() => isFullScreenSliderVisible.value, (value) => {
  lockOrUnlockScroll(value)
})

watch(() => isModalVisible.value, (value) => {
  lockOrUnlockScroll(value)
})

watch(() => sliderIndex.value, (value) => {
  $slider.value.setIndex(value)
})

// Methods
function lockOrUnlockScroll(locked) {
  const method = locked ? 'add' : 'remove'
  document.body.classList[method]('no-scroll')
}

function onEnter($el, done) {
  gsap.from(
    $el.querySelector('.background'),
    {
      autoAlpha: 0,
      duration: 0.6,
      ease: 'expo.out',
    },
  )
  gsap.from(
    $el.querySelector('.box'),
    {
      autoAlpha: 0,
      delay: 0.15,
      duration: 0.6,
      ease: 'expo.out',
      onComplete: () => {
        done()
      },
      scale: 0.9,
      y: 80,
    },
  )
}

function onLeave($el, done) {
  gsap.to(
    $el.querySelector('.box'),
    {
      autoAlpha: 0,
      duration: 0.3,
      ease: 'expo.in',
      scale: 0.9,
      y: 80,
    },
  )
  gsap.to(
    $el.querySelector('.background'),
    {
      autoAlpha: 0,
      delay: 0.075,
      duration: 0.3,
      ease: 'expo.in',
      onComplete: () => {
        done()
      },
    },
  )
}

function onModalFormReady() {
  nextTick(() => {
    $modalForm.value.setHiddenInputValueFor(
      'ebook_name',
      props.page.title,
    )
  })
}

function onPageFormReady() {
  nextTick(() => {
    $pageForm.value.setHiddenInputValueFor(
      'ebook_name',
      props.page.title,
    )
  })
}

function onResize() {
  const { clientHeight, clientWidth } = document.documentElement

  screenHeight.value = clientHeight

  if (screenWidth.value !== clientWidth) {
    const $branding = document.querySelector('.page .nav .branding')

    const navWidth = [...$branding.children].reduce((accumator, $el) => {
      return accumator + $el.offsetWidth
    }, 0)

    const isOverflowing
      = screenWidth.value < 768 ? navWidth > screenWidth.value - 32 : false

    if (isOverflowing) {
      const aspectRatio
        = props.page.coMarketingLogo?.width
          / props.page.coMarketingLogo?.height
      const expectedWidth
        = screenWidth.value
          - 32
          - $branding.children[0].offsetWidth
          - 10.3
          - 24
      const expectedHeight = expectedWidth / aspectRatio
      coMarketingLogoSize.value = expectedHeight
    }
    else {
      coMarketingLogoSize.value = 104
    }

    screenWidth.value = clientWidth
  }
}

async function onSubmitted(submittedValues) {
  // Trigger event to Google Tag Manager
  if (window.dataLayer && form.value.gtmEvent) {
    const { v4: uuidv4 } = await import('uuid')
    dataLayer.push({ event: `${form.value.gtmEvent}.${uuidv4()}` }) // Send event to GTM
  }

  // Trigger event to Segment
  const properties = form.value.segmentWhitelistedProperties
    ? form.value.segmentWhitelistedProperties
        .split(',')
        .map(value => value.trim())
        .reduce((accumulator, value) => {
          return {
            ...accumulator,
            [value]: submittedValues[value] ? submittedValues[value] : undefined,
          }
        }, {})
    : submittedValues

  await track(form.value.segmentEvent || 'Form submitted', {
    ...properties,
    category: 'Form',
    form_id: form.value.formId,
    label: form.value.segmentLabel || form.value.segmentEvent || 'Form submitted',
    locale: locale.value,
    title: window.document.title,
  })

  isDownloadable.value = true
}

// Lifecycle Hooks
onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
})

onMounted(() => {
  window.addEventListener('resize', onResize)
  onResize()
  page(trackName.value)
})
</script>

<style lang="postcss" scoped>
.page {
  @apply relative flex flex-col items-center;

  .nav {
    @apply flex-col items-center min-h-[104px] z-50;

    @screen md {
      @apply flex-row;
    }

    .branding {
      @apply items-center;

      @screen md {
        @apply w-auto;
      }

      .logo {
        @apply h-26 w-auto;

        :deep(img) {
          @apply h-6 w-[143px] max-w-none;
        }
      }

      .other-company {
        @apply flex flex-shrink-0 flex-row items-center h-26 w-auto;

        &::before {
          @apply mx-3 content-['+'] text-lg leading-none;
        }
      }
    }

    .extra {
      @apply justify-center items-center;

      &::before {
        @apply mx-3 content-['+'] text-lg leading-none;
      }

      @screen md {
        @apply justify-start w-auto;
      }
    }

    .action {
      @apply hidden;

      @screen xl {
        @apply flex flex-shrink-0 w-auto ml-auto;
      }
    }
  }

  .jumbotron {
    @apply relative flex-col mt-8;

    @screen md {
      @apply mt-20;
    }

    @screen xl {
      @apply flex-row flex-wrap items-center justify-between mt-10;
    }

    .gradient {
      @apply absolute h-[257.54px] w-[257.54px] z-0;

      @screen md {
        @apply h-[527.44px] w-[527.44px];
      }

      @screen xl {
        @apply h-[763px] w-[763px];
      }

      &.red {
        @apply left-0 top-[54px];
        background: radial-gradient(
          50% 50% at 50% 50%,
          rgba(250, 201, 184, 0.25) 0%,
          rgba(244, 214, 204, 0) 100%
        );

        @screen md {
          @apply -top-[57px];
        }

        @screen xl {
          @apply -left-20 -top-36;
        }
      }

      &.blue {
        @apply right-0 top-[67.16px];
        background: radial-gradient(
          50% 50% at 50% 50%,
          rgba(120, 161, 187, 0.1) 0%,
          rgba(172, 191, 204, 0) 100%
        );

        @screen md {
          @apply -top-[30.04px];
        }

        @screen xl {
          @apply right-[249px] -top-[105px];
        }
      }
    }

    .hero {
      @apply z-10;

      @screen xl {
        @apply w-span6;
      }

      :deep(.h1) {
        @apply text-3xl leading-10;

        @screen mdminus {
          @apply font-medium text-3xl leading-10;
        }

        @screen mdplus {
          @apply font-normal text-5xl leading-16;
        }
      }

      :deep(.description) {
        @apply text-grey-blue-600 text-lg leading-7 whitespace-normal;
      }
    }

    .image {
      @apply mt-12 z-10;

      @screen md {
        @apply mt-18;
      }

      @screen xl {
        @apply justify-end self-center w-span5 mt-0;
      }
    }
  }

  .form {
    @apply flex-col mt-14 z-10;

    @screen md {
      @apply mt-26;
    }

    @screen xl {
      @apply flex-row flex-wrap items-center justify-between mt-16;
    }

    .hero {
      @screen xl {
        @apply w-span5;
      }

      :deep(.description) {
        @apply text-grey-blue-600 text-lg leading-7 whitespace-normal;
      }
    }

    .inner {
      @apply flex-col mt-8 p-6 bg-white shadow-light3 rounded-4xl;

      @screen md {
        @apply mt-12 p-[3.125rem];
      }

      @screen xl {
        @apply w-span6 mt-0 p-12;
      }

      .step {
        @apply flex-col;

        &.one {
          .gdpr {
            @apply mt-4 text-xs text-grey-blue-600 leading-6;

            :deep(a) {
              @apply text-livestorm-blue-700;
            }
          }
        }

        &.two {
          .download {
            @apply flex flex-col p-0;

            &.co-marketing {
              .button {
                @apply w-full;
              }
            }

            .h3 {
              @apply text-2xl leading-8 !important;

              @screen mdminus {
                @apply text-2xl leading-8 !important;
              }

              @screen mdplus {
                @apply text-2xl leading-8 !important;
              }
            }

            .paragraph {
              @apply mt-4 text-grey-blue-600 leading-7;
            }

            .button {
              @apply mt-8;

              @screen xl {
                @apply self-start;
              }

              i {
                @apply text-white mr-2;
              }
            }
          }
        }
      }
    }
  }

  .sample {
    @apply flex-col mt-14;

    @screen md {
      @apply mt-26;
    }

    @screen xl {
      @apply flex-row flex-wrap items-center justify-between;
    }

    .hero {
      @screen xl {
        @apply w-span6;
      }

      :deep(.description) {
        @apply text-grey-blue-600 text-lg leading-7 whitespace-normal;
      }
    }

    .slider {
      @apply mt-8;

      @screen xl {
        @apply order-first w-span5 mt-0;
      }

      :deep(.body) {
        @apply relative cursor-pointer;

        &::before {
          @apply absolute inset-0 opacity-0 transition-opacity duration-500 z-10;
          background-color: rgba(18, 38, 43, 0.1);

          @screen xl {
            @apply content-[''];
          }
        }

        &::after {
          @apply absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-16 w-16 bg-no-repeat bg-center rounded backdrop-blur-[2.5px] opacity-0 transition-opacity duration-500 z-20;
          background-color: rgba(18, 38, 43, 0.6);
          background-image: url("~/assets/svg/zoom.svg");

          @screen xl {
            @apply content-[''];
          }
        }

        &:hover {
          &::before,
          &::after {
            @apply opacity-100;
          }
        }
      }
    }
  }

  .preview {
    @apply justify-center mt-14 py-20 bg-grey-blue-100;

    @screen md {
      @apply py-34 mt-26;
    }

    .inner {
      @apply flex-col;

      @screen xl {
        @apply flex-row flex-wrap items-center justify-between;
      }

      .hero {
        @screen xl {
          @apply w-span6;
        }

        :deep(.description) {
          @apply text-grey-blue-600 text-lg leading-7 whitespace-normal;
        }
      }

      .illustration {
        @apply mt-20;

        @screen xl {
          @apply order-first w-auto mt-0;
        }
      }
    }
  }

  .get-started {
    @apply mt-14;

    @screen md {
      @apply mt-26;
    }

    .call-to-action {
      @apply p-6;

      @screen md {
        @apply p-10;
      }

      @screen xl {
        @apply px-12;
      }

      :deep(.content) {
        .h2 {
          @apply mt-0 font-medium text-xl leading-7 !important;

          @screen md {
            @apply font-medium text-2xl leading-8 !important;
          }

          @screen xl {
            @apply font-normal text-5xl leading-16 !important;
          }
        }

        .buttons {
          .button {
            @apply w-full;

            @screen md {
              @apply w-auto;
            }
          }
        }

        .items {
          @apply hidden;

          @screen xl {
            @apply flex;
          }
        }
      }
    }
  }

  .discover {
    @apply mt-14;

    @screen md {
      @apply mt-26;
    }

    .inner {
      @apply relative flex-col p-6 bg-livestorm-blue-700 rounded-4xl;
      background: url(https://livestorm.imgix.net/1127/1656676680-pattern.png);
      background-size: 1664px;
      background-position-x: 49%;
      background-position-y: 12%;

      @screen md {
        @apply px-12 py-10 bg-none;
        background: linear-gradient(
          90deg,
          rgba(21, 43, 177, 1) 0%,
          rgba(52, 79, 196, 1) 100%
        );
      }

      .ebook {
        @apply hidden;

        @screen md {
          @apply flex absolute bottom-0 -right-[79px] rounded-none z-10;
        }

        @screen xl {
          @apply right-0;
        }
      }

      .h4 {
        @apply text-white z-20;
      }

      .description {
        @apply mt-2 text-grey-blue-100 text-lg leading-7 z-20;

        @screen md {
          @apply max-w-[398px];
        }

        @screen xl {
          @apply max-w-[629px];
        }
      }

      .button {
        @apply self-start mt-2 px-0 h-[50px] font-bold text-white z-20;

        i {
          @apply text-white;
        }
      }
    }
  }

  .related-content {
    @apply justify-center mt-14 py-14 bg-grey-blue-100;

    @screen md {
      @apply py-26 mt-26;
    }

    .inner {
      @apply flex-col;

      .list {
        @apply flex-col mt-12;

        @screen md {
          @apply mt-20;
        }

        @screen xl {
          @apply flex-row flex-wrap items-center justify-between;
        }

        .item {
          @apply mt-10;

          @screen xl {
            @apply flex-col self-start mt-0 h-full;
            width: calc(calc(100% - 5rem) / 3);
          }

          &:first-child {
            @apply mt-0;
          }

          :deep(.head) {
            @screen xl {
              height: 400px;
              width: 400px;
            }
          }

          :deep(.body) {
            @screen xl {
              @apply justify-start h-full p-6;
            }

            a {
              @screen xl {
                @apply mb-auto;
              }
            }

            p {
              @apply hidden !important;
            }
          }
        }
      }
    }
  }

  .popup {
    @apply fixed inset-0 items-center justify-center z-50;

    .background {
      @apply fixed inset-0 h-full w-full;
      background: rgba(18, 38, 43, 0.3);
    }

    .slider {
      @apply relative h-full w-full;

      .close {
        @apply absolute -top-5 -right-5 items-center justify-center h-10 w-10 bg-winter-green-900 rounded-2xl cursor-pointer z-10;

        &:hover {
          .icon {
            @apply rotate-90;
          }
        }

        .icon {
          @apply text-white text-2xl leading-none transform rotate-0 transition-transform duration-500 ease-in-out;
        }
      }

      .inner {
        @apply bg-white rounded-4xl rounded-tr-2xl overflow-hidden z-0;

        .slides {
          @apply max-w-none transition-transform duration-500 ease-in-out;

          .slide {
            @apply opacity-0 rounded-none transition-opacity duration-500 ease-in-out;

            &.active {
              @apply opacity-100;
            }
          }
        }
      }

      .navigation {
        @apply absolute -bottom-[21px] left-1/2 items-center w-auto p-3 bg-winter-green-900 rounded-[24px] transform -translate-x-1/2 z-10;

        .icon {
          @apply text-xl text-grey-blue-600 leading-none cursor-default transition-colors duration-300 ease-in-out;

          &.icon-chevron-left {
            @apply mr-6;
          }

          &.icon-chevron-right {
            @apply ml-6;
          }

          &.active {
            @apply text-white cursor-pointer;
          }
        }

        .bullets {
          .bullet {
            @apply h-3 w-3 mx-2 bg-grey-blue-600 rounded-full cursor-pointer transition-colors duration-300 ease-in-out;

            &:first-child {
              @apply ml-0;
            }

            &:last-child {
              @apply mr-0;
            }

            &.active {
              @apply bg-white cursor-default;
            }
          }
        }
      }
    }
  }

  :deep(.modal) {
    @apply z-[60];

    &.large {
      .box {
        max-width: 752px;
      }
    }

    &.extra-large {
      .box {
        @screen lg {
          max-width: 752px;
        }

        @screen xl {
          max-width: 1208px;
        }
      }
    }

    .box {
      @apply p-6;

      @screen md {
        @apply p-[50px];
      }

      @screen xl {
        @apply p-14;
      }

      .form {
        @apply mt-0;

        .gdpr {
          @apply mt-4 text-xs text-grey-blue-600 leading-6;

          a {
            @apply text-livestorm-blue-700;
          }
        }
      }

      .success {
        @apply w-full;

        .title {
          @apply text-center text-3xl leading-10;

          @screen md {
            @apply text-5xl leading-16;
          }
        }

        .cards {
          @apply flex-col;

          @screen xl {
            @apply flex-row;

            &:only-child {
              @apply justify-center;
            }
          }

          .content-card {
            @apply mt-6;

            @screen md {
              @apply mt-8 h-[264px];
            }

            @screen lg {
              @apply h-auto;
            }

            &:first-child {
              @apply mt-10;
            }

            .head {
              @apply hidden;

              @screen md {
                @apply flex h-[264px];
              }

              @screen lg {
                @apply w-full h-[246px];
              }

              picture {
                @screen lg {
                  @apply w-full h-full;
                }
              }
            }

            .body {
              .button {
                &.no-style {
                  @apply hidden;
                }
              }

              p {
                @apply font-medium text-black text-xl leading-7;
              }
            }

            &.resources {
              &:hover {
                .button {
                  @apply text-white bg-livestorm-blue-800 !important;
                }
              }

              &.split {
                @screen xl {
                  width: calc(1208px - 96px - 424px - 2.5rem);
                }
              }

              .button {
                @apply px-6 h-12 text-white bg-livestorm-blue-700 mt-4;
              }
            }

            &.signup {
              @screen xl {
                @apply flex-grow h-auto w-[424px] mt-10 ml-8;
              }

              .body {
                @screen xl {
                  @apply justify-between h-full pt-0;
                }

                p {
                  @screen md {
                    @apply flex !important;
                  }

                  @screen xl {
                    @apply mt-6;
                    display: -webkit-box !important;
                  }
                }
              }

              .button {
                @apply px-6 h-12 bg-grey-blue-200 text-winter-green-900 mt-4;
              }

              &:hover {
                .button {
                  @apply text-winter-green-900 bg-grey-blue-300 !important;
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>
